import { useEffect, useState } from 'react';
import { FormHelperText, TextField } from '@material-ui/core';
import { Autocomplete, Skeleton } from '@material-ui/lab';
import { DeliveryPoint } from '../../../delivery-points/types/delivery-point.type';
import { MIN_SEARCH_LENGTH } from '../../../shared/constants/search.constants';
import { useSearchDeliveryPoints } from '../../hooks/use-search-delivery-points.hook';
import { useDeliveryPointDeliveryDays } from '../../hooks/use-delivery-point-delivery-days.hook';
import { useLastOrderDeliveryPoint } from '../../hooks/use-last-order-delivery-point.hook';
import { deliveryPointToDeliveryAddress } from '../../utils/delivery-point-to-delivery-address.util';
import { toFormDeliveryType } from '../../constants/delivery-type.constants';
import { NO_DELIVERY_DAY_MESSAGE } from '../../constants/delivery-schedule.constants';
import { DeliveryAddress, DeliveryDay } from '../../types/delivery-selection.type';
import { Order } from '../../types/order.type';
import { DeliveryDaySelect } from './DeliveryDaySelect.component';

type Props = {
  selectedAddress: DeliveryAddress | null;
  selectedDay: DeliveryDay | null;
  onAddressChange: (address: DeliveryAddress | null) => void;
  onBillingAddressChange: (address: DeliveryAddress | null) => void;
  onDayChange: (day: DeliveryDay | null) => void;
  lastOrder?: Order | null;
};

type LastOrderDeliveryPointProps = {
  deliveryPointId: string;
  onLoad: (deliveryPoint: DeliveryPoint) => void;
};

function LastOrderDeliveryPoint({ deliveryPointId, onLoad }: LastOrderDeliveryPointProps) {
  const { deliveryPoint } = useLastOrderDeliveryPoint({ deliveryPointId });

  useEffect(() => {
    if (deliveryPoint && deliveryPoint.archivedAt === null) onLoad(deliveryPoint);
  }, [deliveryPoint, onLoad]);

  return null;
}

const toOptionLabel = (deliveryPoint: DeliveryPoint): string =>
  `${deliveryPoint.name} (${deliveryPoint.address.city} - ${deliveryPoint.address.zipCode})`;

export function RelayDeliverySection({
  selectedAddress,
  selectedDay,
  onAddressChange,
  onBillingAddressChange,
  onDayChange,
  lastOrder,
}: Props) {
  const [query, setQuery] = useState('');
  const [selectedPoint, setSelectedPoint] = useState<DeliveryPoint | null>(null);
  const [hasAppliedLastOrderPoint, setHasAppliedLastOrderPoint] = useState(false);
  const { isSearching, deliveryPoints } = useSearchDeliveryPoints({ query });
  const { isLoading: isLoadingDays, days } = useDeliveryPointDeliveryDays({
    deliveryPointId: selectedPoint?.id ?? null,
  });

  const lastOrderPointId =
    lastOrder && toFormDeliveryType(lastOrder.deliveryInfos.deliveryType) === 'relayPoint'
      ? lastOrder.deliveryInfos.deliveryId
      : null;

  const handleChangePoint = (deliveryPoint: DeliveryPoint | null) => {
    setSelectedPoint(deliveryPoint);
    const address = deliveryPoint ? deliveryPointToDeliveryAddress(deliveryPoint) : null;
    onAddressChange(address);
    onBillingAddressChange(address);
    onDayChange(null);
  };

  const handleLastOrderPointLoad = (deliveryPoint: DeliveryPoint) => {
    setHasAppliedLastOrderPoint(true);
    if (!selectedPoint) handleChangePoint(deliveryPoint);
  };

  useEffect(() => {
    if (selectedPoint && days.length > 0 && !selectedDay) onDayChange(days[0]);
  }, [days, selectedPoint, selectedDay, onDayChange]);

  const handleInputChange = (value: string, reason: string) => {
    if (reason === 'input') setQuery(value);
    if (reason === 'clear') setQuery('');
  };

  const options =
    selectedPoint && !deliveryPoints.some((deliveryPoint) => deliveryPoint.id === selectedPoint.id)
      ? [selectedPoint, ...deliveryPoints]
      : deliveryPoints;

  const renderDeliveryDays = () => {
    if (!selectedPoint || !selectedAddress) return <div />;
    if (isLoadingDays) return <Skeleton />;
    if (days.length === 0) return <FormHelperText error>{NO_DELIVERY_DAY_MESSAGE}</FormHelperText>;
    return <DeliveryDaySelect days={days} selectedDay={selectedDay} onChange={onDayChange} />;
  };

  return (
    <>
      {lastOrderPointId && !hasAppliedLastOrderPoint && (
        <LastOrderDeliveryPoint deliveryPointId={lastOrderPointId} onLoad={handleLastOrderPointLoad} />
      )}
      <Autocomplete<DeliveryPoint, false, false, false>
        options={options}
        value={selectedPoint}
        loading={isSearching}
        filterOptions={(availableOptions) => availableOptions}
        getOptionLabel={toOptionLabel}
        getOptionSelected={(option, value) => option.id === value.id}
        onInputChange={(event, value, reason) => handleInputChange(value, reason)}
        onChange={(event, deliveryPoint) => handleChangePoint(deliveryPoint)}
        noOptionsText={
          query.trim().length < MIN_SEARCH_LENGTH ? 'Tapez au moins deux caractères' : 'Aucun point de livraison'
        }
        loadingText="Recherche en cours…"
        clearText="Effacer"
        openText="Ouvrir"
        closeText="Fermer"
        renderInput={(params) => <TextField {...params} label="Point de livraison" variant="outlined" size="small" />}
      />

      {renderDeliveryDays()}
    </>
  );
}
